import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, FileText, Trash2, GitCompare, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { usePerioExamList, usePerioExam, useDeletePerioExam } from '@/hooks/usePerioExams';
import { computePSRSextants, computePerioDiagnosis } from '@/utils/perio';
import type { SextantKey, PerioMeasurement } from '@/utils/perio';
import { SEXTANTS } from '@/utils/perio';
import { PsrSextantGrid } from './PsrSextantGrid';
import { PerioGrid } from './PerioGrid';
import type { CellValue } from './PerioGrid';
import { PerioDiagnosisCard } from './PerioDiagnosisCard';
import { PerioTimeline } from './PerioTimeline';
import { PerioComparisonView } from './PerioComparisonView';
import { exportPerioPdf } from './PerioExportPdf';

// ── Props ─────────────────────────────────────────────────────────────────────

interface PerioPanelProps {
  patientId: string;
  clinicId: string;
  patientName?: string | null;
}

// ── Helper: cells → PerioMeasurement[] ───────────────────────────────────────

function cellsToPerioMeasurements(cells: Record<string, CellValue>): PerioMeasurement[] {
  return Object.entries(cells).map(([key, cell]) => {
    const [tooth_fdi, site] = key.split(':');
    return {
      tooth_fdi,
      site: site as PerioMeasurement['site'],
      pocket_depth_mm: cell.pocket_depth_mm,
      recession_mm: cell.recession_mm,
      cal_mm: (cell.pocket_depth_mm ?? 0) + (cell.recession_mm ?? 0),
      bleeding_on_probing: cell.bleeding_on_probing,
      suppuration: cell.suppuration,
      mobility: cell.mobility,
      furcation: cell.furcation,
    };
  });
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PerioPanel({ patientId, patientName }: PerioPanelProps) {
  const { t } = useTranslation();

  // ── State ──────────────────────────────────────────────────────────────────

  const [activeExamId, setActiveExamId] = useState<string | null>(null);
  const [compareExamIds, setCompareExamIds] = useState<string[]>([]);
  const [showComparison, setShowComparison] = useState(false);
  const [highlightedSextant, setHighlightedSextant] = useState<SextantKey | null>(null);
  const [liveCells, setLiveCells] = useState<Record<string, CellValue>>({});

  // ── Data ───────────────────────────────────────────────────────────────────

  const { data: examList = [], isLoading: listLoading } = usePerioExamList(patientId);
  const { data: activeExamMeasurements = [], isLoading: examLoading } = usePerioExam(activeExamId);
  const deleteExam = useDeletePerioExam();

  // ── Live measurements (from grid cells while editing) ──────────────────────

  const liveMeasurements: PerioMeasurement[] = useMemo(
    () => cellsToPerioMeasurements(liveCells),
    [liveCells],
  );

  // PSR computed from live data
  const psrScores = useMemo(
    () => computePSRSextants(liveMeasurements.length > 0 ? liveMeasurements : activeExamMeasurements),
    [liveMeasurements, activeExamMeasurements],
  );

  // Diagnosis: use all historical exams
  const historicalExams = useMemo(() => {
    return examList.map(item => {
      // For the active exam, use live cells if we have them
      const measurements =
        item.exam_id === activeExamId && liveMeasurements.length > 0
          ? liveMeasurements
          : activeExamMeasurements;
      return {
        examId: item.exam_id,
        recordedAt: item.recorded_at,
        measurements: item.exam_id === activeExamId ? measurements : [],
      };
    });
  }, [examList, activeExamId, liveMeasurements, activeExamMeasurements]);

  const diagnosis = useMemo(
    () => computePerioDiagnosis(historicalExams.filter(e => e.measurements.length > 0)),
    [historicalExams],
  );

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleNewExam = useCallback(() => {
    setActiveExamId(null);
    setLiveCells({});
  }, []);

  const handleSelectExam = useCallback((examId: string) => {
    setActiveExamId(examId);
  }, []);

  const handleToggleCompare = useCallback((examId: string) => {
    setCompareExamIds(prev => {
      if (prev.includes(examId)) return prev.filter(id => id !== examId);
      if (prev.length >= 2) return [prev[1], examId];
      return [...prev, examId];
    });
  }, []);

  const handleDeleteExam = useCallback(async () => {
    if (!activeExamId) return;
    if (!confirm(t('dental.perio.confirmDelete', 'Slet denne undersøgelse? Dette kan ikke fortrydes.'))) return;
    try {
      await deleteExam.mutateAsync({ patientId, examId: activeExamId });
      setActiveExamId(null);
      toast.success(t('dental.perio.examDeleted', 'Undersøgelse slettet'));
    } catch {
      toast.error(t('dental.perio.examDeleteError', 'Fejl ved sletning'));
    }
  }, [activeExamId, patientId, deleteExam, t]);

  const handleExportPdf = useCallback(() => {
    const measurements =
      liveMeasurements.length > 0 ? liveMeasurements : activeExamMeasurements;
    if (measurements.length === 0) {
      toast.error(t('dental.perio.export.pdf', 'Ingen data at eksportere'));
      return;
    }
    const exam = examList.find(e => e.exam_id === activeExamId);
    exportPerioPdf(
      {
        examId: activeExamId ?? 'new',
        recordedAt: exam?.recorded_at ?? new Date().toISOString(),
        recordedByName: exam?.recorded_by_name ?? null,
        measurements,
      },
      { id: patientId, full_name: patientName },
    );
  }, [liveMeasurements, activeExamMeasurements, examList, activeExamId, patientId, patientName, t]);

  const handleSextantClick = useCallback((key: SextantKey) => {
    setHighlightedSextant(prev => (prev === key ? null : key));
  }, []);

  // Sextant teeth set for highlighting in grid
  const highlightedTeeth: Set<string> | undefined = useMemo(() => {
    if (!highlightedSextant) return undefined;
    return new Set(SEXTANTS[highlightedSextant]);
  }, [highlightedSextant]);

  // Active exam label
  const activeExamLabel = useMemo(() => {
    if (!activeExamId) return t('dental.perio.newExam');
    const idx = examList.findIndex(e => e.exam_id === activeExamId);
    const n = idx >= 0 ? examList.length - idx : 1;
    const date = examList[idx]
      ? new Date(examList[idx].recorded_at).toLocaleDateString('da-DK', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : '';
    return t('dental.perio.examLabel', { n, defaultValue: `Undersøgelse #${n}` }) + (date ? ` — ${date}` : '');
  }, [activeExamId, examList, t]);

  // ── Render ─────────────────────────────────────────────────────────────────

  if (listLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header strip */}
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 bg-white px-6 py-3 dark:border-gray-700 dark:bg-gray-900">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            {t('dental.perio.title')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{activeExamLabel}</p>
        </div>

        <div className="ml-auto flex items-center gap-2">
          {/* New exam */}
          <button
            type="button"
            onClick={handleNewExam}
            className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <Plus className="h-4 w-4" />
            {t('dental.perio.newExam')}
          </button>

          {/* Compare (if 2 exams selected) */}
          {compareExamIds.length === 2 && (
            <button
              type="button"
              onClick={() => setShowComparison(true)}
              className="flex items-center gap-1.5 rounded-lg border border-indigo-300 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-50 dark:border-indigo-600 dark:text-indigo-400"
            >
              <GitCompare className="h-4 w-4" />
              {t('dental.perio.compare.title')}
            </button>
          )}

          {/* Export PDF */}
          <button
            type="button"
            onClick={handleExportPdf}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            <FileText className="h-4 w-4" />
            {t('dental.perio.export.pdf')}
          </button>

          {/* Delete exam */}
          {activeExamId && (
            <button
              type="button"
              onClick={handleDeleteExam}
              disabled={deleteExam.isPending}
              className="flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-700 dark:text-red-400"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Main area */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* PSR overview */}
          <div className="mb-4">
            <PsrSextantGrid
              scores={psrScores}
              highlighted={highlightedSextant}
              onSextantClick={handleSextantClick}
            />
          </div>

          {/* Grid */}
          {examLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
            </div>
          ) : (
            <PerioGrid
              patientId={patientId}
              examId={activeExamId}
              initialMeasurements={activeExamMeasurements}
              highlightedSextantTeeth={highlightedTeeth}
              onExamIdChange={id => setActiveExamId(id)}
              onMeasurementsChange={setLiveCells}
            />
          )}
        </div>

        {/* Right sidebar */}
        <div className="flex w-80 shrink-0 flex-col gap-4 overflow-y-auto border-l border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
          {/* Diagnosis card */}
          <PerioDiagnosisCard diagnosis={diagnosis} />

          {/* Timeline */}
          <PerioTimeline
            exams={examList}
            activeExamId={activeExamId}
            compareExamIds={compareExamIds}
            onSelectExam={handleSelectExam}
            onToggleCompare={handleToggleCompare}
          />
        </div>
      </div>

      {/* Comparison slide-over */}
      {showComparison && compareExamIds.length === 2 && (
        <PerioComparisonView
          examIdA={compareExamIds[0]}
          examIdB={compareExamIds[1]}
          labelA={`Eks. ${examList.length - examList.findIndex(e => e.exam_id === compareExamIds[0])}`}
          labelB={`Eks. ${examList.length - examList.findIndex(e => e.exam_id === compareExamIds[1])}`}
          onClose={() => setShowComparison(false)}
        />
      )}
    </div>
  );
}

// ── PerioRoute ─────────────────────────────────────────────────────────────────

interface PerioRouteProps {
  patientId: string;
  clinicId: string;
  patientName?: string | null;
}

export function PerioRoute({ patientId, clinicId, patientName }: PerioRouteProps) {
  return <PerioPanel patientId={patientId} clinicId={clinicId} patientName={patientName} />;
}
